import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, EnvelopeSimple, DeviceMobile, ArrowLeft, CheckCircle } from "@phosphor-icons/react";

const fieldCls = "mt-1 w-full h-12 px-4 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary";
const labelCls = "text-xs font-bold uppercase tracking-[0.15em] text-black/60";
const btnPrimary = "press w-full h-12 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm disabled:opacity-60";

export const ForgotPasswordFlow = ({ open, onClose }) => {
  const [step, setStep] = useState("identify"); // identify | choose | email_sent | sms_code
  const [identifier, setIdentifier] = useState("");
  const [channels, setChannels] = useState([]);
  const [emailHint, setEmailHint] = useState("");
  const [phoneHint, setPhoneHint] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep("identify"); setIdentifier(""); setChannels([]); setEmailHint(""); setPhoneHint("");
    setDemoCode(""); setCode(""); setNewPw(""); setConfirmPw(""); setErr(""); setBusy(false);
  };
  const close = () => { reset(); onClose(); };

  if (!open) return null;

  const lookup = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/password/forgot", { identifier });
      const chs = data.channels || [];
      setChannels(chs);
      if (chs.length === 1) await sendChannel(chs[0].type);
      else setStep("choose");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const sendChannel = async (ch) => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/password/forgot", { identifier, channel: ch });
      if (ch === "email") { setEmailHint(data.hint || ""); setStep("email_sent"); }
      else { setPhoneHint(data.hint || ""); if (data.demo_mode && data.demo_code) setDemoCode(data.demo_code); setStep("sms_code"); }
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e?.preventDefault();
    setErr("");
    if (newPw.length < 6) { setErr("Choose a password with at least 6 characters."); return; }
    if (newPw !== confirmPw) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/password/reset", { identifier, code, new_password: newPw });
      toast.success("Password updated — sign in with your new password.");
      close();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" data-testid="forgot-password-modal">
      <div className="w-full max-w-md bg-white border-2 border-black rounded-xl hard-shadow p-6 relative">
        <button data-testid="forgot-close-btn" onClick={close} className="press absolute right-4 top-4 text-black/50"><X size={22} weight="bold" /></button>
        <h2 className="font-display text-xl font-bold mb-1">Reset your password</h2>

        {step === "identify" && (
          <form onSubmit={lookup} className="space-y-4 mt-3" data-testid="forgot-identify-form">
            <p className="text-sm text-black/60">Enter your username, email, or mobile number and we'll help you reset it.</p>
            <div>
              <label className={labelCls}>Username, email or phone</label>
              <input data-testid="forgot-identifier-input" type="text" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                value={identifier} onChange={(e) => setIdentifier(e.target.value)} required className={fieldCls} placeholder="your username, email or +1808…" />
            </div>
            {err && <p data-testid="forgot-error" className="text-sm font-bold text-primary">{err}</p>}
            <button data-testid="forgot-continue-btn" type="submit" disabled={busy} className={btnPrimary}>{busy ? "Checking…" : "Continue"}</button>
          </form>
        )}

        {step === "choose" && (
          <div className="space-y-3 mt-3" data-testid="forgot-choose">
            <p className="text-sm text-black/60">How would you like to reset it?</p>
            {channels.map((c) => (
              <button key={c.type} data-testid={`forgot-channel-${c.type}`} disabled={busy} onClick={() => sendChannel(c.type)}
                className="press w-full h-14 rounded-lg bg-white border-2 border-black flex items-center gap-3 px-4 text-left disabled:opacity-60">
                {c.type === "email" ? <EnvelopeSimple size={22} weight="bold" /> : <DeviceMobile size={22} weight="bold" />}
                <span>
                  <span className="block text-sm font-bold">{c.type === "email" ? "Email me a reset link" : "Text me a reset code"}</span>
                  <span className="block text-xs text-black/50">{c.hint}</span>
                </span>
              </button>
            ))}
            {err && <p data-testid="forgot-error" className="text-sm font-bold text-primary">{err}</p>}
            <button type="button" onClick={reset} className="press inline-flex items-center gap-1 text-xs font-bold text-black/60"><ArrowLeft size={14} weight="bold" /> Start over</button>
          </div>
        )}

        {step === "email_sent" && (
          <div className="space-y-4 mt-3 text-center" data-testid="forgot-email-sent">
            <CheckCircle size={44} weight="fill" className="text-primary mx-auto" />
            <p className="text-sm text-black/70">We emailed a reset link to <span className="font-bold">{emailHint}</span>. Open it and choose a new password. The link expires in 1 hour.</p>
            <button data-testid="forgot-done-btn" onClick={close} className={btnPrimary}>Done</button>
          </div>
        )}

        {step === "sms_code" && (
          <form onSubmit={doReset} className="space-y-4 mt-3" data-testid="forgot-sms-form">
            <p className="text-sm text-black/60">We texted a 6-digit code to <span className="font-bold">{phoneHint}</span>. Enter it and set a new password.</p>
            <div>
              <label className={labelCls}>Verification code</label>
              <input data-testid="forgot-code-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required
                className={`${fieldCls} tracking-[0.5em] text-center font-bold text-lg`} placeholder="••••••" />
            </div>
            {demoCode && (
              <p data-testid="forgot-demo-code" className="text-xs font-bold text-black/70 bg-[#FEF9C3] border-2 border-black rounded-lg p-2.5">
                Demo mode (no SMS provider configured): your code is <span className="text-primary">{demoCode}</span>.
              </p>
            )}
            <div>
              <label className={labelCls}>New password</label>
              <input data-testid="forgot-newpw-input" type="password" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required className={fieldCls} placeholder="At least 6 characters" />
            </div>
            <div>
              <label className={labelCls}>Confirm new password</label>
              <input data-testid="forgot-confirmpw-input" type="password" autoComplete="new-password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required className={fieldCls} placeholder="Re-enter password" />
            </div>
            {err && <p data-testid="forgot-error" className="text-sm font-bold text-primary">{err}</p>}
            <button data-testid="forgot-reset-btn" type="submit" disabled={busy} className={btnPrimary}>{busy ? "Updating…" : "Reset password"}</button>
            <button type="button" onClick={reset} className="press inline-flex items-center gap-1 text-xs font-bold text-black/60"><ArrowLeft size={14} weight="bold" /> Start over</button>
          </form>
        )}
      </div>
    </div>
  );
};
